/**
 * Calculate the distance between two coordinates using the Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in miles
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 3959; // Radius of the Earth in miles
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
}

/**
 * Convert degrees to radians
 * @param {number} degrees
 * @returns {number} Radians
 */
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Filter and sort locations by distance from a given point
 * @param {Array} locations - Array of location objects with latitude and longitude
 * @param {number} userLat - User's latitude
 * @param {number} userLon - User's longitude
 * @param {number} maxDistance - Maximum distance in miles (default: 10)
 * @param {number} limit - Maximum number of results to return (default: 20)
 * @returns {Array} Filtered and sorted array of locations with distance property
 */
export function filterLocationsByDistance(locations, userLat, userLon, maxDistance = 10, limit = 20) {
  if (!locations || locations.length === 0) {
    return [];
  }

  // Calculate distance for each location and add it as a property
  const locationsWithDistance = locations.map(location => ({
    ...location,
    distance: calculateDistance(userLat, userLon, location.latitude, location.longitude)
  }));

  // Filter by max distance and sort by distance (closest first)
  const filteredAndSorted = locationsWithDistance
    .filter(location => location.distance <= maxDistance)
    .sort((a, b) => a.distance - b.distance);

  // Return only the specified number of closest locations
  return filteredAndSorted.slice(0, limit);
}

/**
 * Geocode a street address string into lat/lon using the Google Maps Geocoding API.
 * Returns { latitude, longitude } or null if geocoding fails.
 * @param {string} addressString - Full address string (e.g. "123 Main St, Miami, FL 33101")
 * @param {string} apiKey - Google Maps API key
 * @returns {Promise<{latitude: number, longitude: number}|null>}
 */
export async function geocodeAddress(addressString, apiKey) {
  try {
    const encoded = encodeURIComponent(addressString);
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encoded}&key=${apiKey}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data.status === 'OK' && data.results.length > 0) {
      const { lat, lng } = data.results[0].geometry.location;
      return { latitude: lat, longitude: lng };
    }
  } catch (e) {
    // geocoding failed, return null gracefully
  }
  return null;
}

/**
 * Get only the closest locations without distance filtering
 * Useful when you want top N closest regardless of distance
 * @param {Array} locations - Array of location objects with latitude and longitude
 * @param {number} userLat - User's latitude
 * @param {number} userLon - User's longitude
 * @param {number} limit - Maximum number of results to return (default: 20)
 * @returns {Array} Sorted array of closest locations with distance property
 */
export function getClosestLocations(locations, userLat, userLon, limit = 20) {
  if (!locations || locations.length === 0) {
    return [];
  }

  // Calculate distance for each location
  const locationsWithDistance = locations.map(location => ({
    ...location,
    distance: calculateDistance(userLat, userLon, location.latitude, location.longitude)
  }));

  // Sort by distance and take top N
  return locationsWithDistance
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit);
}
